import { SHEET_ACTION_PROGRAMMATICALLY, type ActivationAction } from "../event/event.action.defs";
import type { ActivationReason } from "./stack.index.js";

const stackData = $state<{
  prev: string[];
  current: string | null;
  previous: string | null;
  visited: string[];
  reason: ActivationReason;
  contentChange: boolean;
  isPushing: boolean;
  isPopping: boolean;
  /**
 *
 */
  popLength: number;
  action: ActivationAction;
  info: unknown;
  isPresentationStart: boolean;
  isPresentationEnd: boolean;
}>({
  prev: [],
  current: null,
  previous: null,
  visited: [],
  reason: "open-close",
  contentChange: false,
  isPushing: false,
  isPopping: false,
  popLength: 0,
  action: SHEET_ACTION_PROGRAMMATICALLY,
  info: undefined,
  isPresentationStart: false,
  isPresentationEnd: false,
});

/*                   */
/*                                                                     */
/*  */

/**
 *
 */
export const stack = stackData as Readonly<typeof stackData>;

export const computeReason = <IsChange extends boolean>(
  isChange: IsChange,
): IsChange extends true ? "content-switch" : "open-close" =>
  (isChange ? "content-switch" : "open-close") as never;

const withLock = <A extends unknown[]>(
  fn: (...args: A) => void,
): ((...args: A) => typeof stack) => {
  return (...args) => {
    /*                                                                    */
    fn(...args);

    /*                                                                 */
    stackData.previous = stackData.prev.at(-1) ?? null;

    /*                                                                                                  */
    if (stackData.previous && stackData.current) {
      stackData.contentChange = true;
    }
    /*                                                                                  */
    /*                */
    else if (!stackData.isPushing && !stackData.isPopping) {
      stackData.contentChange = false;

      /*                                                           */
      /*                                                                         */
      if (!stack.current) {
        stackData.popLength = 0;
      }
    }
    /*                                                                  */
    /*                              */
    else if (!stack.current) {
      stackData.contentChange = false;
    }

    /*                                               */
    stackData.reason = computeReason(stackData.contentChange);

    /*                                                                        */
    stackData.isPresentationStart =
      !!stack.current && stack.prev.length === 0 && stack.visited.length === 0;
    stackData.isPresentationEnd =
      !stack.current && stack.prev.length === 0 && stack.visited.length > 0 && !stack.isPopping;

    return stack;
  };
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const updatePopLength: (prev: string[]) => void = (prev) => {
  /*                                                                        */
  stackData.popLength = stackData.current !== null ? prev.length + 1 : prev.length;
  /*                                                      */
};

/**
 *
 */
const _commitPopPush = (): void => {
  stackData.isPushing = false;
  stackData.isPopping = false;
  stackData.action = SHEET_ACTION_PROGRAMMATICALLY;
};

/**
 *
 *
 */
export const commitPopPush = withLock(_commitPopPush);

const _clearStack = (): void => {
  stackData.prev = [];
  stackData.current = null;
  stackData.visited = [];
  _commitPopPush();
};

export const clearStack = withLock(_clearStack);

export const restoreStack = withLock(
  (
    currentId: string | undefined,
    currentPrev: string[] | undefined,
    action: ActivationAction = SHEET_ACTION_PROGRAMMATICALLY,
  ) => {
    stackData.current = currentId ?? null;
    stackData.prev = currentPrev ? [...currentPrev] : [];
    stackData.action = action;
  },
);

/**
 *
 *
 *
 */
export const setInfo = (info: unknown): typeof stack => {
  stackData.info = info;
  return stack;
};

export const pushStack = withLock((id: string, action: ActivationAction, info: unknown) => {
  const { prev, current } = stackData;
  if (current === id) return stackData; /*                  */

  /*          */
  if (current !== null) prev.push(current);
  stackData.current = id;
  updatePopLength(prev);

  stackData.isPushing = true;
  stackData.action = action;
  stackData.info = info;
});

export const popStack = withLock((action: ActivationAction, info: unknown) => {
  const { visited, prev, current } = stackData;

  if (current === null) {
    if (prev.length > 0) {
      /*             */
      return _clearStack();
    }

    /*              */
    return stack;
  }

  /*                      */
  visited.unshift(current);
  /*                    */
  stackData.current = prev.pop() ?? null;
  updatePopLength(prev);

  stackData.isPopping = true;
  stackData.action = action;
  stackData.info = info;
});

export const popStackAll = withLock((action: ActivationAction, info: unknown) => {
  const { visited, prev, current } = stackData;

  /*                              */
  if (current === null) return stack;

  /*                                                */
  /*                                      */
  updatePopLength(prev);

  visited.unshift(current);
  visited.unshift(...prev.splice(0));

  stackData.current = null;
  stackData.isPopping = true;
  stackData.action = action;
  stackData.info = info;
});

export const deleteStackItem = withLock((id: string) => {
  /*                          */
  if (stackData.current === id) return _clearStack();

  if (stackData.prev.includes(id)) stackData.prev.splice(stackData.prev.indexOf(id), 1);
  if (stackData.visited.includes(id)) stackData.visited.splice(stackData.visited.indexOf(id), 1);

  updatePopLength(stackData.prev);
});

export const getStackAllIds = (): string[] => {
  const { prev, current, visited } = stackData;
  /*                                                       */
  return [...new Set([...prev, current, ...visited].filter((id): id is string => id !== null))];
};
